import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const FAQs = ({ user, onLogout }) => {
  const faqCategories = [
    {
      category: 'General Questions',
      questions: [
        {
          q: 'What age groups do you teach?',
          a: 'We offer programs for all ages! Our Learn to Swim programs start from 6 months for children, and we have adult lessons for beginners of any age. Our competitive Marlins swim team accepts swimmers aged 6-18.',
        },
        {
          q: 'Do I need to know how to swim to join?',
          a: 'Not at all! We have programs specifically designed for complete beginners. Our instructors will assess your comfort level and place you in the appropriate class.',
        },
        {
          q: 'What should I bring to my first lesson?',
          a: 'Bring a swimsuit, towel, goggles, and swim cap. That is all you need! For children, these items are sufficient to get started with our program.',
        },
        {
          q: 'Are your facilities accessible?',
          a: 'Our facility is located inside Achimota Secondary School campus and is open to the public during operating hours. We are working on improving accessibility features for all our clients.',
        },
      ],
    },
    {
      category: 'Booking & Payment',
      questions: [
        {
          q: 'How do I book a lesson?',
          a: 'You can book online through our website by clicking "Book Now" and selecting your preferred program, date, and time. You\'ll receive instant confirmation via email.',
        },
        {
          q: 'What payment methods do you accept?',
          a: 'We accept Mobile Money (MTN, Vodafone, AirtelTigo), bank cards (Visa/Mastercard), and cash payments at the facility. Online payments are processed securely through Paystack.',
        },
        {
          q: 'Can I pay in installments?',
          a: 'Yes! For packages and memberships, we offer flexible payment plans. Contact us to discuss installment options that work for your budget.',
        },
        {
          q: 'Do you offer family discounts?',
          a: 'Absolutely! Families with multiple members enrolling receive a 10% discount on the second enrollment and 15% on third and subsequent enrollments.',
        },
      ],
    },
    {
      category: 'Classes & Programs',
      questions: [
        {
          q: 'How long are the lessons?',
          a: 'Children\'s lessons are 30-45 minutes, adult lessons are 45-60 minutes, and competitive team training sessions are 90 minutes. Class duration varies by program and skill level.',
        },
        {
          q: 'What is your student-to-instructor ratio?',
          a: 'We maintain small class sizes for personalized attention. Beginner classes have a maximum of 4-6 students per instructor, while advanced classes may have up to 8 students.',
        },
        {
          q: 'Can I request a specific instructor?',
          a: 'Yes, you can request a preferred instructor based on availability. We\'ll do our best to accommodate your request when you book.',
        },
        {
          q: 'What if my child is afraid of water?',
          a: 'We specialize in working with children who have water anxiety. Our patient instructors use gentle, encouraging methods to build confidence gradually. Many of our most confident swimmers started with a fear of water!',
        },
      ],
    },
    {
      category: 'Safety & Health',
      questions: [
        {
          q: 'What are your safety protocols?',
          a: 'Safety is our top priority. We have certified lifeguards on duty during all operating hours, maintain rigorous pool sanitization standards, and all instructors are CPR and first aid certified.',
        },
        {
          q: 'What is your water quality standard?',
          a: 'Our pools are tested multiple times daily and maintained to international standards. We use advanced filtration systems and maintain optimal chemical balance for swimmer safety and comfort.',
        },
        {
          q: 'What should I do if my child is sick?',
          a: 'Please keep sick children at home to prevent spreading illness. Contact us to reschedule your lesson.',
        },
      ],
    },
    {
      category: 'Cancellations & Refunds',
      questions: [
        {
          q: 'What is your cancellation policy?',
          a: 'You can cancel or reschedule a lesson up to 24 hours in advance without penalty. Cancellations with less than 24 hours notice will forfeit that lesson.',
        },
        {
          q: 'Can I get a refund?',
          a: 'Refunds are available within 48 hours of purchase for unused lessons or memberships. After 48 hours, we offer credits or transfers to another program instead of refunds.',
        },
        {
          q: 'What if classes are cancelled due to weather or facility issues?',
          a: 'In case of facility closure or severe weather, we will notify you immediately and offer makeup sessions or credit to your account. Safety always comes first.',
        },
        {
          q: 'Can I transfer my package to someone else?',
          a: 'Yes, packages can be transferred to family members. Contact our office to process the transfer. A small administrative fee may apply.',
        },
      ],
    },
    {
      category: 'Marlins Swim Team',
      questions: [
        {
          q: 'How do I join the Marlins swim team?',
          a: 'Swimmers must be able to swim at least 25 meters unassisted and be committed to regular training. We conduct assessments to place swimmers in the appropriate training group.',
        },
        {
          q: 'What is the training schedule?',
          a: 'The Marlins train 3-5 times per week depending on the swimmer\'s level. Training times are scheduled in the afternoons and weekends to accommodate school schedules.',
        },
        {
          q: 'Do team members compete?',
          a: 'Yes! We participate in local, regional, and national competitions throughout the year. Competition is optional but highly encouraged for skill development.',
        },
        {
          q: 'What equipment do team members need?',
          a: 'Team members need a competition swimsuit, goggles, swim cap, and training fins. Team uniforms are provided. We can help you source quality equipment at discounted rates.',
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen">
      <Navigation user={user} onLogout={onLogout} />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-linear-to-br from-cyan-50 to-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Find answers to common questions about our programs, facilities, and services
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          {faqCategories.map((category, catIndex) => (
            <motion.div
              key={catIndex}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: catIndex * 0.1 }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold text-slate-900 mb-6">{category.category}</h2>
              
              <Accordion type="single" collapsible className="space-y-4">
                {category.questions.map((faq, qIndex) => (
                  <AccordionItem
                    key={qIndex}
                    value={`${catIndex}-${qIndex}`}
                    className="bg-slate-50 rounded-xl px-6 border-none"
                    data-testid={`faq-item-${catIndex}-${qIndex}`}
                  >
                    <AccordionTrigger className="text-left hover:no-underline py-5">
                      <span className="text-lg font-semibold text-slate-900 pr-4">
                        {faq.q}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600 leading-relaxed pb-5">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Still Have Questions?
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Can't find the answer you're looking for? Our friendly team is here to help!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact">
                <button className="btn-primary">
                  Contact Us
                </button>
              </a>
              <a href="tel:+233201358015">
                <button className="btn-secondary">
                  Call Us Now
                </button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQs;
